import { useQuery } from "@tanstack/react-query";
import React from "react";

import { QueryCacheKey } from "../../../api";
import {
  CenterAlign,
  Drawer,
  LoaderWithPerfTimings,
  Paper,
  PaperContent,
  Typography,
} from "../../../components";
import { ImportDetailsSidebarContent } from "../../../components/ImportDetailsSidebarContent";
import { useApiContext } from "../../../utils";

type Props = {
  dataImportId: string | undefined;
  onClose: (value: string | undefined) => void;
};

export const DataImportDetailsDrawer: React.FC<React.PropsWithChildren<Props>> = ({
  dataImportId,
  onClose,
}) => {
  const { apiClient } = useApiContext();

  const {
    data: dataImportResponse,
    isLoading: isLoadingDataImport,
    error: errorDataImport,
  } = useQuery(
    [QueryCacheKey.DATA_IMPORT, dataImportId],
    () => apiClient.getDataImport({ data_import_id: dataImportId! }),
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: Boolean(dataImportId),
    }
  );

  const {
    data: spreadsheetsResponse,
    isLoading: isLoadingSpreadsheets,
    error: errorSpreadsheets,
  } = useQuery([QueryCacheKey.DASHBOARD_SPREADSHEETS], () => apiClient.getDashboardSpreadsheets());

  const dataImport = dataImportResponse?.data.data_import;

  const spreadsheet = spreadsheetsResponse?.data.spreadsheets?.find(
    s => s.google_sheets_file_id === dataImport?.target_info?.google_sheets_file_id
  );

  const renderContent = () => {
    if (isLoadingDataImport || isLoadingSpreadsheets) {
      return (
        <CenterAlign>
          <LoaderWithPerfTimings name="DataImportDetailsDrawer" size="large" />
        </CenterAlign>
      );
    }

    if (errorDataImport) {
      return (
        <CenterAlign>
          <Paper>
            <PaperContent>
              <Typography fontWeight="bold" gutterBottom>
                Error
              </Typography>
              <Typography>Unable to load import</Typography>
            </PaperContent>
          </Paper>
        </CenterAlign>
      );
    }

    if (dataImport === undefined) {
      return <noscript />;
    }

    return (
      <ImportDetailsSidebarContent
        dataImport={dataImport}
        spreadsheet={spreadsheet}
        onClose={() => onClose(undefined)}
      />
    );
  };

  return (
    <Drawer
      open={dataImportId !== undefined}
      setOpen={open => {
        if (!open) {
          onClose(undefined);
        }
      }}
      closeOnClickAway={false}
    >
      {renderContent()}
    </Drawer>
  );
};
